import React, { useState, useEffect } from 'react';
import { getOccupiers, updateOccupier } from '@/lib/api';
import { Users, Loader2, DoorOpen, FileText, Key, Pencil, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function OccupiersPage() {
  const [occupiers, setOccupiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOcc, setEditOcc] = useState(null);

  const loadData = () => { getOccupiers().then(setOccupiers).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => { loadData(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="occupiers-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Occupiers</h1>
        <p className="text-sm text-[#5C5C55] mt-1">{occupiers.length} registered practitioners</p>
      </div>

      <div className="space-y-3">
        {occupiers.map((occ, i) => (
          <div key={occ.user_id}
            className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-5 sm:p-6 animate-fade-in stagger-${(i % 6) + 1}`}
            data-testid={`occupier-card-${occ.user_id}`}>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#E8EDE5] flex items-center justify-center text-sm font-semibold text-[#3A4035] shrink-0">
                {occ.name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-heading text-base font-medium text-[#1A1A1A]">{occ.name}</h3>
                    <p className="text-xs text-[#8A8B85]">{occ.business_name}{occ.business_type ? ` — ${occ.business_type}` : ''}</p>
                  </div>
                  <button onClick={() => setEditOcc(occ)}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors flex items-center gap-1.5 shrink-0"
                    data-testid={`edit-occupier-${occ.user_id}`}>
                    <Pencil className="w-3 h-3" strokeWidth={1.5} />Edit
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {occ.studio && (
                    <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#E8EDE5] text-[#3A4035] font-medium">
                      <DoorOpen className="w-3 h-3" strokeWidth={1.5} />{occ.studio.name}
                    </span>
                  )}
                  {occ.contract && (
                    <span className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${occ.contract.status === 'active' ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#F4EFE6] text-[#7A6849]'}`}>
                      <FileText className="w-3 h-3" strokeWidth={1.5} />{occ.contract.status?.replace('_', ' ')}
                    </span>
                  )}
                  {occ.access && (
                    <span className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${occ.access.status === 'active' ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#F4EFE6] text-[#7A6849]'}`}>
                      <Key className="w-3 h-3" strokeWidth={1.5} />{occ.access.status}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#8A8B85] mt-2">{occ.email} · {occ.phone}</p>
                {occ.last_updated_by_name && (
                  <p className="text-[10px] text-[#8A8B85] mt-1 italic">Last updated by {occ.last_updated_by_name}{occ.updated_at ? ` on ${new Date(occ.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {occupiers.length === 0 && (
        <div className="text-center py-16">
          <Users className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#8A8B85]">No occupiers yet</p>
        </div>
      )}

      {/* Edit Modal */}
      {editOcc && <EditOccupierModal open={!!editOcc} onClose={() => setEditOcc(null)} occupier={editOcc}
        onSaved={() => { setEditOcc(null); loadData(); }} />}
    </div>
  );
}

function EditOccupierModal({ open, onClose, occupier, onSaved }) {
  const [form, setForm] = useState({
    name: occupier.name || '',
    email: occupier.email || '',
    phone: occupier.phone || '',
    business_name: occupier.business_name || '',
    business_type: occupier.business_type || '',
    home_address: occupier.home_address || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError(''); setSaved(false); };

  useEffect(() => {
    if (open) {
      setForm({
        name: occupier.name || '', email: occupier.email || '', phone: occupier.phone || '',
        business_name: occupier.business_name || '', business_type: occupier.business_type || '',
        home_address: occupier.home_address || '',
      });
      setError(''); setSaved(false); setSaving(false);
    }
  }, [open, occupier]);

  const emailChanged = form.email.trim().toLowerCase() !== (occupier.email || '').toLowerCase();
  const canSave = form.name.trim() && form.email.trim() && form.email.includes('@');

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!canSave) { setError('Name and valid email are required'); return; }
    setSaving(true); setError('');
    try {
      await updateOccupier(occupier.user_id, {
        name: form.name.trim(), email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(), business_name: form.business_name.trim(),
        business_type: form.business_type.trim(), home_address: form.home_address.trim(),
      });
      setSaved(true);
      setTimeout(() => onSaved(), 800);
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to update');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="edit-occupier-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Edit Occupier — {occupier.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">Update practitioner details</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]" data-testid="edit-error">
            <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">{error}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4 pt-1">
          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name <span className="text-[#B33A3A]">*</span></Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="edit-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">
                Email <span className="text-[#B33A3A]">*</span>
                {emailChanged && <span className="text-[#8A7230] ml-1">(changing)</span>}
              </Label>
              <Input value={form.email} onChange={e => set('email', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="edit-email" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
              <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="edit-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Business Name</Label>
              <Input value={form.business_name} onChange={e => set('business_name', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="edit-business-name" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Service Type</Label>
              <Input value={form.business_type} onChange={e => set('business_type', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="edit-business-type" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Home Address</Label>
            <Input value={form.home_address} onChange={e => set('home_address', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="edit-address" />
          </div>

          {emailChanged && (
            <div className="bg-[#FDFAF0] rounded-xl p-3 border border-[#EDE4C8] flex items-center gap-2" data-testid="email-warning">
              <AlertCircle className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} />
              <p className="text-xs text-[#8A7230]">Changing the email will affect this occupier's login credentials.</p>
            </div>
          )}

          <Button type="submit" disabled={saving || !canSave || saved}
            className={`w-full rounded-full h-11 text-sm font-medium transition-all ${saved ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#3A4035] text-white hover:bg-[#2B3027]'}`}
            data-testid="save-occupier-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : saved ? <><Check className="w-4 h-4 mr-2" strokeWidth={1.5} />Saved</> : <>Save Changes</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
